import dayjs, { Dayjs } from 'dayjs'
import { addVat } from '../../utils/priceUtils'
import { trpc } from '../../modules/trpc'
import { SpotPriceDto } from '@vae/server/src/resources/spotPrice/spotPriceSchemas'
import { useMemo } from 'react'

export type SpotPrice = {
    deliveryStart: Dayjs
    deliveryEnd: Dayjs
    centsPerKwh: number
}

const spotPriceDtoToSpotPrice = (dto: SpotPriceDto): SpotPrice => ({
    centsPerKwh: dto.eurosPerMwh / 10,
    deliveryStart: dayjs(dto.deliveryStart),
    deliveryEnd: dayjs(dto.deliveryEnd),
})

export interface UseDayAheadPricesResult {
    dayAheadPrices: SpotPrice[]
    isLoading: boolean
}

/**
 * Cut off "extra" spot prices that barely land on the next day, leaving the day incomplete.
 * 
 * @param cutoffHour How many hours need to be filled for a day to count "full enough" to show.
 */
const cutTimezoneTail = (dayAheadPrices: SpotPrice[], cutoffHour = 12): SpotPrice[] => {
    const lastValidHour = dayAheadPrices.findLastIndex(price =>
        price.deliveryStart.local().hour() >= cutoffHour
    )
    return dayAheadPrices.slice(0, lastValidHour + 1)
}

export interface UseDayAheadPricesOptions {
    from: Dayjs
    to: Dayjs
    biddingZone?: string
    includeVat?: boolean
    /** Timespan to secretly fetch on the background. */
    cacheSpan?: [Dayjs, Dayjs]
}

/**
 * Fetch day-ahead prices of 15-minute resolution.
 * 
 */
export const useDayAheadPrices = ({
    from,
    to,
    biddingZone = 'FI',
    includeVat = true,
    cacheSpan,
}: UseDayAheadPricesOptions): UseDayAheadPricesResult => {
    const { data, isLoading } = trpc.spotPrice.dayAheadPrices.useQuery({
        from: cacheSpan ? cacheSpan[0].utc().toISOString() : from.utc().toISOString(),
        to: cacheSpan ? cacheSpan[1].utc().toISOString() : to.utc().toISOString(),
        biddingZone,
    }, {
        staleTime: 60 * 60 * 1000, // 1 hour
    })

    const dayAheadPrices = useMemo(() => {
        if (!data || isLoading) {
            return []
        }

        let prices = data
            .map(spotPriceDtoToSpotPrice)
            .filter(price => price.deliveryStart.isSameOrAfter(from) && price.deliveryStart.isBefore(to))

        prices = cutTimezoneTail(prices)

        if (includeVat) {
            prices = prices.map(price => ({
                ...price,
                centsPerKwh: addVat(price.centsPerKwh),
            }))
        }

        return prices
    }, [data, isLoading, from, to, includeVat])

    return { dayAheadPrices, isLoading }
}
